'use client';

import { isEqual } from 'lodash';
import { twMerge } from 'tailwind-merge';
import { useRef, useState } from 'react';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { Form, Formik, FormikValues } from 'formik';
import { Box, Stack, useTheme, Container } from '@mui/material';

import { getBrandingLogo } from 'utils/branding';

import { AuthLayoutProps } from './types';

export function AuthLayout<Value extends FormikValues>({
  title,
  children,
  onSubmit,
  className,
  logoHidden,
  initialValues,
  ...formikProps
}: AuthLayoutProps<Value>) {
  const theme = useTheme();

  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const valuesRef = useRef<Value>(initialValues);

  const resetMessages = () => {
    setErrorMessage('');
    setSuccessMessage('');
  };

  const onChangeValues = (values: Value) => {
    const isChanged = !isEqual(values, valuesRef.current);
    valuesRef.current = values;
    if (isChanged) {
      resetMessages();
    }
  };

  const renderedMessage = errorMessage || successMessage;

  return (
    <Box
      className={className}
      sx={{
        display: 'flex',
        minHeight: '100vh',
        alignItems: 'center',
        flexDirection: 'column',
        justifyContent: 'center',
      }}
    >
      <Container maxWidth="sm" component="main">
        <Stack
          sx={{
            p: 4,
            gap: 1,
            display: 'flex',
            borderRadius: 1,
            border: '1px solid',
            alignItems: 'center',
            flexDirection: 'column',
            bgcolor: 'background.paper',
            boxShadow: theme.shadows[4],
            borderColor: alpha(theme.palette.grey[400], 0.4),
          }}
        >
          <div className="flex flex-col items-center gap-4">
            {!logoHidden && (
              <img alt="Logo" className="h-16" src={getBrandingLogo()} />
            )}
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
              }}
            >
              {title}
            </Typography>
          </div>

          {renderedMessage && (
            <div
              className={twMerge(
                'mb-3 flex items-center gap-3 rounded-xl px-4 py-3',
                !!successMessage && 'bg-green-500',
                !!errorMessage && 'bg-red-500'
              )}
            >
              <Typography>{renderedMessage}</Typography>
            </div>
          )}

          <div className="flex w-full items-center justify-center">
            <Formik<Value>
              {...formikProps}
              initialValues={initialValues}
              onSubmit={async (values) => {
                resetMessages();
                const result = onSubmit(values);
                if (result instanceof Promise) {
                  const promiseResult = await result;
                  if (promiseResult && typeof promiseResult === 'object') {
                    if ('successMessage' in promiseResult) {
                      setSuccessMessage(promiseResult.successMessage);
                    }
                    if ('errorMessage' in promiseResult) {
                      setErrorMessage(promiseResult.errorMessage);
                    }
                  }
                  return promiseResult;
                } else {
                  return result;
                }
              }}
            >
              {(props) => {
                const render =
                  // @ts-ignore
                  children instanceof Function ? children(props) : children;

                onChangeValues(props.values);

                return (
                  <div className="w-full">
                    <Form>{render}</Form>
                  </div>
                );
              }}
            </Formik>
          </div>
        </Stack>
      </Container>
    </Box>
  );
}
